import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {
  envIsIntegration,
  envIsLocal,
  serverEnableBackendGrouping,
} from '../../../config';
import {RampIdType} from '../../../generated/graphql';
import {getDatadogLogs} from '../../datadog/datadogLogs';
import {getDatadogRum} from '../../datadog/datadogRum';
import {logFeatureFlagEvaluation} from '../../datadog/hooks';
import {
  useEntityFlagFromStore,
  useOrgFlagFromStore,
  useUserFlagFromStore,
  useUserOrOrgFlagFromStore,
} from './useRampFlagStore';

/**
 * Store-backed translations of the hooks in `util/rampFeatureFlags/
 * linePlotFeatureFlags.ts`. Each function preserves the original logic -
 * env/server checks, boolean combinators, nullish fallbacks - and only swaps
 * the Apollo primitives for the route-hydrated store readers. Datadog RUM
 * context tagging stays inline with each combinator (master pattern, one
 * synchronous `setGlobalContextProperty` call per route hydrate), but the
 * helper now consults a module-level cache so repeated identical writes
 * (re-mounts, multiple combinators tagging the same key) are skipped.
 *
 * `entityName` parameters were dropped: the entity flag store is hydrated
 * from the current route's `useProjectParams`, so callers no longer need
 * to thread it through.
 *
 * The originals in `linePlotFeatureFlags.ts` are still in place; migrate
 * call sites incrementally by swapping imports.
 */

// Stable singleton; safe to capture once at module load. Used only inside the
// route-level init effects, never on the per-panel render path.
const DD_RUM = getDatadogRum();
const DD_LOGS = getDatadogLogs();

/**
 * Caches `useViewerFeatureFlagsQuery` results as O(1) rampKey -> boolean maps
 * (pre-evaluated at init time, one map per rampIDType). Readers do a single
 * Record lookup and no Datadog work.
 */
type LinePlotFlagsState = {
  isBackendSmoothingEnabled: boolean;
  isBackendGroupingEnabled: boolean;
  isNonMonotonicXAxisEnabled: boolean;
  isLineConstructionProfilingEnabled: boolean;
  // Tri-state preserved for `?? true`/`?? false` call sites.
  isFullFidelityRangesEnabled: boolean | undefined;
  isFullFidelityRunsPerChartEnabled: boolean;
  isIncreaseFullScreenMaxRunsEnabled: boolean;
  isDynamicFullScreenBinSizingEnabled: boolean;
  isBackendLineConstructionEnabled: boolean;
  isBackendLineConstructionGroupedEnabled: boolean;
  isBackendLineConstructionSmoothingEnabled: boolean;
  isBackendLineConstructionTimeUnitsEnabled: boolean;
  isBackendLineConstructionReportsEnabled: boolean;
  isCombinedMetricsRunsLimitUIEnabled: boolean;
  isClientSideDynamicTimeoutsEnabled: boolean;
};

const INITIAL_LINE_PLOT_FLAGS_STATE: LinePlotFlagsState = {
  isBackendSmoothingEnabled: false,
  isBackendGroupingEnabled: false,
  isNonMonotonicXAxisEnabled: false,
  isLineConstructionProfilingEnabled: false,
  isFullFidelityRangesEnabled: undefined,
  isFullFidelityRunsPerChartEnabled: false,
  isIncreaseFullScreenMaxRunsEnabled: false,
  isDynamicFullScreenBinSizingEnabled: false,
  isBackendLineConstructionEnabled: false,
  isBackendLineConstructionGroupedEnabled: false,
  isBackendLineConstructionSmoothingEnabled: false,
  isBackendLineConstructionTimeUnitsEnabled: false,
  isBackendLineConstructionReportsEnabled: false,
  isCombinedMetricsRunsLimitUIEnabled: false,
  isClientSideDynamicTimeoutsEnabled: false,
};

export const useLinePlotFlagsStore = create<LinePlotFlagsState>()(
  devtools(() => INITIAL_LINE_PLOT_FLAGS_STATE, {name: 'LinePlotFlagsStore'})
);

export const useInitializeLinePlotFlagsStore = () => {
  const isBackendSmoothingEnabled = useRampFlagUseBackendSmoothing();
  const isBackendGroupingEnabled = useEnableBackendGrouping();
  const isNonMonotonicXAxisEnabled = useRampFlagNonMonotonicXAxis();
  const isLineConstructionProfilingEnabled =
    useRampFlagLineConstructionProfiling();
  const isFullFidelityRangesEnabled = useRampFlagFullFidelityRanges();
  const isFullFidelityRunsPerChartEnabled =
    useRampFlagFullFidelityRunsPerChart();
  const isIncreaseFullScreenMaxRunsEnabled =
    useRampFlagIncreaseFullScreenMaxRuns();
  const isDynamicFullScreenBinSizingEnabled =
    useRampFlagDynamicFullScreenBinSizing();
  const isBackendLineConstructionEnabled =
    useStatsigGateBackendLineConstruction();
  const isBackendLineConstructionGroupedEnabled =
    useStatsigGateBackendLineConstructionGrouped();
  const isBackendLineConstructionSmoothingEnabled =
    useStatsigGateBackendLineConstructionSmoothing();
  const isBackendLineConstructionTimeUnitsEnabled =
    useStatsigGateBackendLineConstructionTimeUnits();
  const isBackendLineConstructionReportsEnabled =
    useStatsigGateBackendLineConstructionReports();
  const isCombinedMetricsRunsLimitUIEnabled =
    useStatsigGateCombinedMetricsRunsLimitUI();
  const isClientSideDynamicTimeoutsEnabled =
    useRampFlagClientsideDynamicTimeouts();

  // This is a valid Effect: it synchronizes React hook outputs into the
  // external Zustand store after render.
  // https://react.dev/reference/react/useEffect
  useEffect(() => {
    useLinePlotFlagsStore.setState({
      isBackendSmoothingEnabled,
      isBackendGroupingEnabled,
      isNonMonotonicXAxisEnabled,
      isLineConstructionProfilingEnabled,
      isFullFidelityRangesEnabled,
      isFullFidelityRunsPerChartEnabled,
      isIncreaseFullScreenMaxRunsEnabled,
      isDynamicFullScreenBinSizingEnabled,
      isBackendLineConstructionEnabled,
      isBackendLineConstructionGroupedEnabled,
      isBackendLineConstructionSmoothingEnabled,
      isBackendLineConstructionTimeUnitsEnabled,
      isBackendLineConstructionReportsEnabled,
      isCombinedMetricsRunsLimitUIEnabled,
      isClientSideDynamicTimeoutsEnabled,
    });
  }, [
    isBackendSmoothingEnabled,
    isBackendGroupingEnabled,
    isNonMonotonicXAxisEnabled,
    isLineConstructionProfilingEnabled,
    isFullFidelityRangesEnabled,
    isFullFidelityRunsPerChartEnabled,
    isIncreaseFullScreenMaxRunsEnabled,
    isDynamicFullScreenBinSizingEnabled,
    isBackendLineConstructionEnabled,
    isBackendLineConstructionGroupedEnabled,
    isBackendLineConstructionSmoothingEnabled,
    isBackendLineConstructionTimeUnitsEnabled,
    isBackendLineConstructionReportsEnabled,
    isCombinedMetricsRunsLimitUIEnabled,
    isClientSideDynamicTimeoutsEnabled,
  ]);
};

/**
 * Module-level cache of the last value we wrote to Datadog RUM for each
 * context key. Mirrors the dedupe pattern in `util/datadog/hooks.ts`
 * (`lastLoggedFeatureFlagValues`). Two layers of dedupe:
 *   1. React's own `[name, value]` deps stop the effect from re-running
 *      when the value didn't change inside a single mounted hook instance.
 *   2. This module-level cache stops a re-mount (or a different combinator
 *      tagging the same key) from re-emitting an identical write.
 * Persistent for the page lifetime - we never `removeGlobalContextProperty`,
 * since late RUM events from this view should still carry the flag value.
 */
const lastSetGlobalContextValues = new Map<string, unknown>();

/**
 * Keeps a Datadog RUM global-context property in sync with `value` via a
 * `useEffect`. Safe to call from any combinator hook on the render path -
 * the per-key cache above guarantees we only hit the SDK when the value
 * has actually changed since the last write for that key.
 */
function useDatadogRumContextProperty(
  name: string,
  value: boolean | undefined
) {
  useEffect(() => {
    if (
      lastSetGlobalContextValues.has(name) &&
      Object.is(lastSetGlobalContextValues.get(name), value)
    ) {
      return;
    }
    lastSetGlobalContextValues.set(name, value);
    DD_RUM.setGlobalContextProperty(name, value);
    logFeatureFlagEvaluation(DD_RUM, DD_LOGS, name, value);
  }, [name, value]);
}

const useRampFlagUseBackendSmoothing = () => {
  const isDeploymentEnabled = useOrgFlagFromStore(
    'backend_smoothing_deployment',
    RampIdType.OrgId
  );
  const isUserEnabled = useUserFlagFromStore(
    'backend_smoothing_user',
    RampIdType.UserName
  );
  const isEntityEnabled = useEntityFlagFromStore(
    'backend_smoothing_entity',
    RampIdType.EntityName
  );
  const value = envIsLocal
    ? (isDeploymentEnabled ?? false)
    : isUserEnabled || isEntityEnabled;
  useDatadogRumContextProperty('backend_smoothing', value);
  return value;
};

const useEnableBackendGrouping = () => {
  const value = serverEnableBackendGrouping();
  useDatadogRumContextProperty('use_backend_grouping', value);
  return value;
};

const useRampFlagNonMonotonicXAxis = () => {
  const userOrOrgFlag = useUserOrOrgFlagFromStore(
    'non-monotonic-x-axis-user',
    'non-monotonic-x-axis-org'
  );
  const entityFlag = useEntityFlagFromStore(
    'non-monotonic-x-axis-entity',
    RampIdType.EntityName
  );

  return userOrOrgFlag || entityFlag;
};

const useRampFlagLineConstructionProfiling = () => {
  return useEntityFlagFromStore(
    'line-construction-profiling',
    RampIdType.EntityName
  );
};

const useRampFlagFullFidelityRanges = () => {
  const isDeploymentEnabled = useOrgFlagFromStore(
    'full_fidelity_ranges_deployment',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityFlagFromStore(
    'full_fidelity_ranges_entity',
    RampIdType.EntityName
  );
  const isUserEnabled = useUserFlagFromStore(
    'full_fidelity_ranges_user',
    RampIdType.UserName
  );
  if (envIsLocal) {
    return isDeploymentEnabled ?? true;
  }
  return isEntityEnabled || isUserEnabled;
};

/**
 * This currently doesn't support an entity flag because I didn't want to do the work to thread and entity value into the check. It probably won't be that hard but we're just experimenting currently.
 */
const useRampFlagFullFidelityRunsPerChart = () => {
  const value = useUserOrOrgFlagFromStore(
    'full_fidelity_run_per_chart_user',
    'full_fidelity_run_per_chart_org'
  );
  useDatadogRumContextProperty('full_fidelity_run_per_chart', value);
  return value;
};

/**
 * Combined feature flag for higher full-screen line plot max runs.
 * True if either the org-level or user-level Statsig gate is enabled
 * (backend: increase_full_screen_max_runs_org, increase_full_screen_max_runs_user).
 */
const useRampFlagIncreaseFullScreenMaxRuns = () => {
  const isUserEnabled = useUserFlagFromStore(
    'increase_full_screen_max_runs_user',
    RampIdType.UserId
  );
  const isOrgEnabled = useOrgFlagFromStore(
    'increase_full_screen_max_runs_org',
    RampIdType.OrgName
  );
  const value = isUserEnabled || isOrgEnabled || false;
  useDatadogRumContextProperty('increase_full_screen_max_runs', value);
  return value;
};

/**
 * Org-level flag for dynamic full-screen bin sizing.
 * When enabled, the full-screen 10k query uses Math.min(width * 4, 10000)
 * instead of a hardcoded 10,000 bins, matching uPlot's decimation threshold.
 */
const useRampFlagDynamicFullScreenBinSizing = () => {
  const isOrgEnabled = useOrgFlagFromStore(
    'dynamic_full_screen_bin_sizing_org',
    RampIdType.OrgId
  );
  const value = isOrgEnabled || false;
  useDatadogRumContextProperty('dynamic_full_screen_bin_sizing', value);
  return value;
};

const useStatsigGateBackendLineConstruction = () => {
  const isUserEnabled = useUserFlagFromStore(
    'backend_line_construction_user',
    RampIdType.UserId
  );
  const isOrgEnabled = useOrgFlagFromStore(
    'backend_line_construction_org',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityFlagFromStore(
    'backend_line_construction_entity',
    RampIdType.EntityId
  );
  const value = isUserEnabled || isOrgEnabled || isEntityEnabled;
  // Master used the literal string 'backend_line_construction_user' here even
  // though the value combines user/org/entity flags - preserve verbatim so
  // existing dashboards keep working.
  useDatadogRumContextProperty('backend_line_construction_user', value);
  return value;
};

const useStatsigGateBackendLineConstructionGrouped = () => {
  const value = useEntityFlagFromStore(
    'backend_line_construction_grouped_entity',
    RampIdType.EntityId
  );
  useDatadogRumContextProperty('backend_line_construction_grouped', value);
  return value;
};

const useStatsigGateBackendLineConstructionSmoothing = () => {
  const value = useEntityFlagFromStore(
    'backend_line_construction_smoothing',
    RampIdType.EntityName
  );
  useDatadogRumContextProperty('backend_line_construction_smoothing', value);
  return value;
};

const useStatsigGateBackendLineConstructionTimeUnits = () => {
  const value = useEntityFlagFromStore(
    'backend_line_construction_time_units',
    RampIdType.EntityName
  );
  useDatadogRumContextProperty('backend_line_construction_time_units', value);
  return value;
};

const useStatsigGateBackendLineConstructionReports = () => {
  const isOrgEnabled = useOrgFlagFromStore(
    'backend_line_construction_reports_org',
    RampIdType.OrgId
  );
  const isEntityEnabled = useEntityFlagFromStore(
    'backend_line_construction_reports_entity',
    RampIdType.EntityId
  );
  const value = isOrgEnabled || isEntityEnabled;
  useDatadogRumContextProperty('backend_line_construction_reports', value);
  return value;
};

const useStatsigGateCombinedMetricsRunsLimitUI = () => {
  const isEntityEnabled = useEntityFlagFromStore(
    'combined_metrics_runs_limit_ui',
    RampIdType.EntityId
  );

  const value = envIsIntegration ? false : isEntityEnabled;
  useDatadogRumContextProperty('combined_metrics_runs_limit_ui', value);
  return value;
};

const useRampFlagClientsideDynamicTimeouts = () => {
  const value = useEntityFlagFromStore(
    'clientside_dynamic_timeouts_entity',
    RampIdType.EntityId
  );
  useDatadogRumContextProperty('clientside_dynamic_timeouts_entity', value);
  return value;
};
